import capitalize from 'lodash/capitalize'
import { Link } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import Card from 'app/frontend/base/components/Card'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { useCharacterClasses } from 'app/frontend/base/hooks/useCharacterClasses'
import { armorTypeIcons, armorTypes } from 'app/frontend/types/custom/ArmorType'
import { characterRoles } from 'app/frontend/types/custom/CharacterRole'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import { cn } from '@/lib/utils'
import { useActiveTeams } from '../hooks'

type CandidateFilter = (candidate: wowaudit.GuildCandidate) => boolean

type Column = {
  key: string
  label: string
  filter: CandidateFilter
  rosterPath?: string
}

type Row = {
  key: string
  label: React.ReactNode
  filter: CandidateFilter
}

const SlantedHeader = ({
  label,
  height,
  muted,
}: {
  label: string
  height: number
  muted?: boolean
}) => (
  <th className='p-0 align-bottom'>
    <div
      style={{ height: `${height}rem` }}
      className='w-8 flex items-end justify-center'
    >
      <div className='relative'>
        <span
          className={cn(
            'absolute bottom-0 right-1/2 rotate-[35deg] whitespace-nowrap max-w-28 text-ellipsis overflow-hidden font-normal text-xs',
            muted && 'text-muted-foreground',
          )}
          style={{ transformOrigin: 'center right' }}
        >
          {label}
        </span>
      </div>
    </div>
  </th>
)

const CountTable = ({
  title,
  rows,
  columns,
  candidates,
}: {
  title: string
  rows: Row[]
  columns: Column[]
  candidates: wowaudit.GuildCandidate[]
}) => {
  const headerHeight = Math.max(
    Math.min(
      4.5,
      Math.max(...columns.map((column) => column.label.length)) / 2.5,
    ),
    1.5,
  )

  return (
    <Card className='w-full' size='sm'>
      <table className='w-full'>
        <thead>
          <tr>
            <th className='text-left align-bottom font-normal text-xs'>
              {title}
            </th>
            {columns.map((column) => (
              <SlantedHeader
                key={column.key}
                label={column.label}
                height={headerHeight}
                muted={column.key === 'no-team'}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const rowCandidates = candidates.filter(row.filter)

            return (
              <tr key={row.key} className='border-t'>
                <td>
                  <div
                    className={cn(
                      'flex items-center gap-1.5 mr-2 whitespace-nowrap',
                      rowCandidates.length > 0
                        ? ''
                        : '[&_span]:opacity-60 [&_span]:italic',
                    )}
                  >
                    {row.label}
                  </div>
                </td>
                {columns.map((column) => {
                  const count = rowCandidates.filter(column.filter).length

                  return (
                    <td key={column.key} className='text-center w-8'>
                      {count || <span className='opacity-30'>-</span>}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </Card>
  )
}

const CandidateStats = ({
  candidates,
}: {
  candidates: wowaudit.GuildCandidate[]
}) => {
  const teams = useActiveTeams()
  const characterClasses = useCharacterClasses()

  const columns: Column[] = [
    ...teams.map((team) => ({
      key: team.id.toString(),
      label: team.name,
      filter: (candidate: wowaudit.GuildCandidate) =>
        candidate.teamIds.includes(team.id),
      rosterPath: `${team.path}/roster`,
    })),
    {
      key: 'no-team',
      label: 'No team',
      filter: (candidate) => candidate.teamIds.length === 0,
    },
  ]
  return (
    <div className='max-xl:pt-4 xl:border-l-2 xl:pl-4'>
      <h4 className='mb-2 xl:hidden'>Setup</h4>
      <div className='max-sm:grid-cols-1 max-md:grid-cols-2 gap-2 max-xl:grid max-xl:grid-cols-3 xl:flex xl:flex-col'>
        <div className='flex flex-col gap-2 grow'>
          <Card className='w-full' size='sm'>
            <div className='flex items-center gap-2'>
              <span className='font-semibold text-lg leading-none'>
                {candidates.length}
              </span>
              <span>candidate{candidates.length === 1 ? '' : 's'}</span>
            </div>
            <div className='flex flex-col gap-1.5 mt-3 pt-2 border-t'>
              {columns.map((column) => {
                const count = candidates.filter(column.filter).length
                const share = candidates.length
                  ? (count / candidates.length) * 100
                  : 0

                return (
                  <div
                    key={column.key}
                    className='grid grid-cols-[minmax(0,1fr)_4rem_1.5rem] items-center gap-2 text-sm'
                  >
                    {column.rosterPath ? (
                      <Link
                        to={column.rosterPath}
                        className='group flex items-center gap-1 min-w-0 hover:text-accent-foreground'
                      >
                        <span className='truncate'>{column.label}</span>
                        <ArrowUpRight className='h-3.5 w-3.5 shrink-0 text-muted-foreground group-hover:text-foreground transition-colors' />
                      </Link>
                    ) : (
                      <span className='truncate italic text-muted-foreground'>
                        {column.label}
                      </span>
                    )}
                    <div className='h-1.5 rounded-full bg-muted overflow-hidden'>
                      <div
                        className={cn(
                          'h-full rounded-full',
                          column.key === 'no-team'
                            ? 'bg-muted-foreground/50'
                            : 'bg-primary',
                        )}
                        style={{ width: `${share}%` }}
                      />
                    </div>
                    <span className='text-right font-semibold'>
                      {count || <span className='opacity-30'>-</span>}
                    </span>
                  </div>
                )
              })}
            </div>
          </Card>

          <CountTable
            title='Role'
            candidates={candidates}
            columns={columns}
            rows={characterRoles.map((role) => ({
              key: role,
              filter: (candidate) => candidate.role === role,
              label: (
                <span className='flex items-center gap-1.5'>
                  <CharacterRoleIcon role={role} size='small' />
                  {capitalize(role)}
                </span>
              ),
            }))}
          />
        </div>

        <div className='flex flex-col gap-2 [&>div]:grow grow'>
          <CountTable
            title='Armor type'
            candidates={candidates}
            columns={columns}
            rows={Object.entries(armorTypes).map(([armorType, classes]) => ({
              key: armorType,
              filter: (candidate) =>
                classes.includes(candidate.characterReference.characterClass),
              label: (
                <Tooltip>
                  <TooltipTrigger>
                    <span className='flex items-center gap-1.5'>
                      <BlizzardIcon
                        category={56}
                        iconName={armorTypeIcons[armorType]}
                        size='small'
                      />
                      {armorType}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className='flex gap-2'>
                    {classes.map((characterClass) => (
                      <CharacterClassIcon
                        key={characterClass}
                        characterClass={characterClass}
                        size='medium'
                      />
                    ))}
                  </TooltipContent>
                </Tooltip>
              ),
            }))}
          />
        </div>

        <div className='grow'>
          <CountTable
            title='Class'
            candidates={candidates}
            columns={columns}
            rows={characterClasses.map((characterClass) => ({
              key: characterClass,
              filter: (candidate) =>
                candidate.characterReference.characterClass === characterClass,
              label: (
                <span
                  className={cn(
                    'flex items-center gap-1.5',
                    classColours[characterClass].text,
                  )}
                >
                  <CharacterClassIcon
                    characterClass={characterClass}
                    size='small'
                  />
                  {characterClass}
                </span>
              ),
            }))}
          />
        </div>
      </div>
    </div>
  )
}

export default CandidateStats
